import { useInfiniteQuery } from '@tanstack/react-query';
import { publicGet } from '@/lib/api/publicClient';
import { publicJobStories } from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/errors';

export interface PublicJobStory {
  review: {
    platform: string | null;
    platformUrl: string | null;
    source: string;
    rating: number;
    text: string | null;
    author: string | null;
    authorAvatarUrl: string | null;
    postedAt: string;
  };
  job: {
    serviceName: string | null;
    jobTypeName: string | null;
    title: string;
    description: string | null;
    technicianName: string | null;
    completedAt: string | null;
  } | null;
  location: { city: string | null; state: string | null; lat: number | null; lng: number | null };
  notes: Array<{ content: string | null; pinned: boolean }>;
  photos: Array<{ fileName: string; mimeType: string; downloadUrl: string }>;
}

export interface PublicJobStoriesBranding {
  headline: string;
  description: string;
  accentColor: string;
  font: string;
  showMap: boolean;
  showFilters: boolean;
  showAreas: boolean;
  showPhotos: boolean;
  showNotes: boolean;
  showTechnician: boolean;
  showJobDescription: boolean;
  showPlatform: boolean;
}

/** One page of the share, exactly as the endpoint returns it. */
export interface PublicJobStoriesPage {
  stories: PublicJobStory[];
  /**
   * The complete index of service areas — every area the share publishes, not
   * only the ones on this page, so the filter is trustworthy before the reader
   * has loaded everything. Sent once, with the first page; `null` afterwards
   * means "unchanged", which is why it is not an empty array.
   */
  areas: string[] | null;
  /** Where the next page resumes. `null` means this was the last of them. */
  nextCursor: string | null;
  branding: PublicJobStoriesBranding;
}

/** Every page loaded so far, as the one list the page renders. */
export interface PublicJobStoriesPayload {
  stories: PublicJobStory[];
  areas: string[];
  branding: PublicJobStoriesBranding | undefined;
}

/**
 * A retired or forged token is the only failure that says anything about the
 * link itself. A 500 or a dropped connection says nothing — and telling the
 * visitor the link is no longer active sends them to the business with the
 * wrong problem, one nobody can reproduce because the link was fine. Shared so
 * the retry policy and the page agree on what "dead link" means.
 */
export function linkIsRevoked(error: unknown): boolean {
  return isApiError(error) && [401, 403, 404].includes(error.status);
}

/**
 * Exported for its own test: a live query observer is an awkward place to
 * assert what the page finally reads, and this is the whole of that answer.
 *
 * The page reads one list of stories, not a list of pages. Areas and branding
 * come from the first page alone: branding is identical on every page, and the
 * index is only sent once because it already describes the whole share — a
 * later page's `null` means "unchanged", so reading it off the newest page
 * would empty the filter as soon as the reader loaded more.
 *
 * Declared out here rather than inline so its identity is stable across
 * renders; React Query caches a select result against the function it came
 * from, and a fresh closure per render throws that away.
 */
export function selectLoadedPages(data: {
  pages: PublicJobStoriesPage[];
}): PublicJobStoriesPayload {
  return {
    stories: data.pages.flatMap((page) => page.stories),
    areas: data.pages[0]?.areas ?? [],
    branding: data.pages[0]?.branding,
  };
}

export function usePublicJobStories(token: string) {
  return useInfiniteQuery({
    queryKey: ['public', 'job-stories', token],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await publicGet<{ data: PublicJobStoriesPage }>(
        publicJobStories.landing(token),
        pageParam ? { cursor: pageParam } : undefined,
      );
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PublicJobStoriesPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(token),
    // A revoked link will never succeed, so retrying it just delays the
    // answer. A transport failure is the opposite case and is worth one
    // more attempt before the page tells the visitor anything.
    retry: (count: number, error: unknown) => count < 2 && !linkIsRevoked(error),
    staleTime: 60_000,
    select: selectLoadedPages,
  });
}
